// :cow: Cosmose CONFIDENTIAL :iso:
import { Injectable } from '@angular/core';
import { Step } from '@med/app/home/demo/guided-tour/models/step';
import clone from 'clone';
import { Campaign } from '@med/app/home/campaign/model/campaign';
import { Observable, of, zip } from 'rxjs';
import { Store } from '@ngrx/store';
import { concatMap, map } from 'rxjs/operators';
import { CampaignDefault } from '@med/app/shared/models/campaign-defaults/campaign-default';
import { Country } from '@med/app/core/model/country';
import { SpotPlan } from '@med/app/home/campaign/model/spot-plan.model';
import { EstimatesDto } from '@med/app/home/campaign/model/estimates';
import { Demographics } from '@med/app/shared/models/demographics/demographics';
import { PresetTree } from '@med/app/core/services/preset/preset-tree';
import { Client } from '@med/app/auth/model/client';
import { CampaignList } from '@med/app/home/campaign/model/campaign-list';
import { Budget } from '@med/app/home/campaign/model/budget';
import { HierarchicalLocationUnitGroup } from '@med/app/home/campaign/model/hierarchical-location-unit-group';
import { HttpClient } from '@angular/common/http';
import moment from 'moment';
import { AdFormat } from '@med/app/shared/modules/ad-format/model/ad-format.model';
import { Platform } from '@med/app/shared/modules/platform/model/platform';
import { PlannerBasicDemoStepEnum } from '@med/app/home/planner-basic-demo/planner-basic-demo-step.enum';
import { plannerBasicDemoSteps } from '@med/app/home/planner-basic-demo/planner-basic-demo-steps';
import { City } from '../planner-basic/models/city';
import { CompetitorBrand } from '../../shared/models/competitor-brands/competitor-brand';

@Injectable({providedIn: 'root'})
export class PlannerBasicDemoDataProviderService {

  constructor(
    private store: Store,
    private httpClient: HttpClient,
  ) {
  }

  getRecommendedPlatforms(language: string): Observable<Platform[]> {
    return this.getFile<Platform[]>(`${ language }/recommended-platforms.json`);
  }

  getBudget(): Observable<Budget> {
    return this.getFile<Budget>(`budget.json`);
  }

  getClients(): Observable<Client[]> {
    return this.getFile<Client[]>(`clients.json`);
  }

  getCampaignList(id: PlannerBasicDemoStepEnum, language: string): Observable<CampaignList> {
    switch (id) {
      case PlannerBasicDemoStepEnum.campaignListExposeCampaign:
        return zip(
          this.getFile<CampaignList>(`${ language }/campaign-list/exposed-campaign-list.json`),
          this.getCampaign(PlannerBasicDemoStepEnum.summaryAskToClickSendForReview, language),
        ).pipe(
          concatMap(([campaignList, campaign]) => {
            return of({
              ...campaignList,
              content: [campaign],
            });
          }),
        );
      default:
        return this.getFile(`${ language }/campaign-list/empty-campaign-list.json`);
    }
  }

  getStep(id: PlannerBasicDemoStepEnum): Step {
    const step = plannerBasicDemoSteps.find(s => s.stepId === id);
    if (!step) {
      return null;
    }

    return clone(step);
  }

  getCampaign(id: PlannerBasicDemoStepEnum, language: string): Observable<Campaign> {
    return zip(
      this.getFile<Campaign>(`${ language }/campaign/${ this.getBaseCampaignFilename() }`),
      this.getFile<Partial<Campaign>>(`${ language }/campaign/${ this.getCampaignFilename(id) }`),
    ).pipe(
      map(([baseCampaign, campaignStepExtension]) => {
        return {
          ...baseCampaign,
          ...campaignStepExtension,
          ...this.getCampaignDynamicFields(id),
        };
      }),
    );
  }

  getLocationUnitGroups(language: string): Observable<HierarchicalLocationUnitGroup[]> {
    const url = `${ language }/location-unit-groups.json`;

    return this.getFile<HierarchicalLocationUnitGroup[]>(url);
  }

  getCampaignDefaults(): Observable<CampaignDefault[]> {
    const url = `campaign-defaults.json`;

    return this.getFile<CampaignDefault[]>(url);
  }

  getPlatforms(language: string): Observable<Platform[]> {
    const url = `${ language }/platforms.json`;

    return this.getFile<Platform[]>(url);
  }

  getCities(language: string): Observable<City[]> {
    const url = `${ language }/cities.json`;

    return this.getFile<City[]>(url);
  }

  getCompetitorBrands(language: string): Observable<CompetitorBrand[]> {
    const url = `${ language }/competitor-brands.json`;

    return this.getFile<CompetitorBrand[]>(url);
  }

  getSpotplan(): Observable<SpotPlan> {
    return of(null);
  }

  getAdformats(language: string): Observable<AdFormat[]> {
    const url = `${ language }/adformats.json`;

    return this.getFile<AdFormat[]>(url);
  }

  getEstimates(id: PlannerBasicDemoStepEnum): Observable<EstimatesDto> {
    switch (id) {
      case PlannerBasicDemoStepEnum.campaignListAddNewCampaign:
      case PlannerBasicDemoStepEnum.campaignObjectiveSelectNewStoreOpening:
      case PlannerBasicDemoStepEnum.plannerStartSelectAudience:
      case PlannerBasicDemoStepEnum.audienceShowEstimates:
      case PlannerBasicDemoStepEnum.audienceReadSegmentDescriptions:
        return this.getFile('estimates/1.json');
      case PlannerBasicDemoStepEnum.audienceShowEstimatesChange:
        return this.getFile('estimates/2.json');
      default:
        return this.getFile('estimates/2.json');
    }
  }

  getDemographics(language: string): Observable<Demographics> {
    const url = `${ language }/demographics.json`;

    return this.getFile<Demographics>(url);
  }

  getCampaignPresets(language: string): Observable<PresetTree> {
    const url = `${ language }/presets.json`;

    return this.getFile<PresetTree>(url);
  }

  getCountries(language: string): Observable<Country[]> {
    const url = `${ language }/countries.json`;

    return this.getFile<Country[]>(url);
  }

  private getBaseCampaignFilename(): string {
    return 'base-campaign.json';
  }

  private getCampaignDynamicFields(id: PlannerBasicDemoStepEnum): Partial<Campaign> {
    switch (id) {
      case PlannerBasicDemoStepEnum.summaryAskToClickSendForReview:
      case PlannerBasicDemoStepEnum.budgetCalculationExplanation:
      case PlannerBasicDemoStepEnum.budgetSelectCampaignDuration: {
        const dateFormat = 'YYYY-MM-DD';

        return {
          timeline: {
            from: moment().add(1, 'day').format(dateFormat),
            to: moment().add(15, 'day').format(dateFormat),
          },
        };
      }
      default:
        return {};
    }
  }

  private getCampaignFilename(id: PlannerBasicDemoStepEnum): string {
    switch (id) {
      case PlannerBasicDemoStepEnum.demographicsSelectGender:
        return '1.json';
      case PlannerBasicDemoStepEnum.budgetSelectCampaignDuration:
        return '2.json';
      case PlannerBasicDemoStepEnum.budgetCalculationExplanation:
        return '3.json';
      case PlannerBasicDemoStepEnum.mediaSearchPlatform:
        return '4.json';
      case PlannerBasicDemoStepEnum.sentForReviewAskToClickBackToDashboard:
        return '5.json';
      case PlannerBasicDemoStepEnum.summaryAskToClickSendForReview:
        return '6.json';
      case PlannerBasicDemoStepEnum.audienceExplainAddingLocationCategories:
        return '7.json';
      case PlannerBasicDemoStepEnum.audienceShowTimespan:
        return '8.json';
      case PlannerBasicDemoStepEnum.demographicsAskToClickContinue:
        return '9.json';
      case PlannerBasicDemoStepEnum.mediaAskToClickContinue:
        return '10.json';
      default:
        return this.getBaseCampaignFilename();
    }
  }

  private getFile<T>(path: string): Observable<T> {
    const url = `${ window.location.origin }/assets/planner-demo/japan/${ path }`;

    return this.httpClient.get<T>(url);
  }
}
